import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Flag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

const LOGO_WHITE = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/a9pwlfiw_white_text_racing.png";
const LOGO_DARK = "https://customer-assets-jai6qajn.emergentagent.net/job_type-race-4/artifacts/ne0g788w_Text-RAcing-04-02-e1599230884415.png";

export default function Auth({ mode, admin }) {
  const isLogin = mode === "login";
  const { login, register, logout } = useAuth();
  const { settings } = useSettings();
  const isLight = settings.theme === "light"
    || (settings.theme === "system" && typeof window !== "undefined"
        && window.matchMedia && window.matchMedia("(prefers-color-scheme: light)").matches);
  const logo = isLight ? LOGO_DARK : LOGO_WHITE;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const next = params.get("next") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = isLogin
      ? await login(email, password)
      : await register(name, email, password, ref);
    if (res.ok && admin && res.user?.role !== "admin") {
      await logout();
      setBusy(false);
      setError("This portal is for IBRA administrators only.");
      return;
    }
    setBusy(false);
    if (res.ok) {
      toast.success(admin ? "Welcome, IBRA Official" : isLogin ? "Welcome back to the grid" : "License account created - 500 credits added");
      navigate(admin ? "/admin" : (next || "/dashboard"));
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="max-w-md mx-auto mt-6 animate-slide-up">
      <div className="flex flex-col items-start gap-4 mb-6">
        <img src={logo} alt="Text Racing" className="h-10 w-auto" />
        <h1 className="font-heading text-5xl tracking-tight">{admin ? "IBRA ADMIN" : isLogin ? "SIGN IN" : "JOIN THE LEAGUE"}</h1>
        {admin && <p className="text-race-sub text-sm -mt-2">Authorized IBRA officials only. This portal is not for public sign-up.</p>}
      </div>
      {!isLogin && ref && (
        <div className="mb-4 bg-race-surface border border-race-red/40 px-4 py-3 rounded-sm" data-testid="referral-banner">
          <p className="text-sm text-race-txt">🏁 You were invited to a <span className="text-race-red font-mono">Racing Team</span> - sign up to join.</p>
        </div>
      )}
      <form onSubmit={submit} className="bg-race-surface border border-race-line p-6 flex flex-col gap-4">
        {!isLogin && (
          <div>
            <label className="overline block mb-1">Racer Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="auth-name"
              className="w-full bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-race-txt" />
          </div>
        )}
        <div>
          <label className="overline block mb-1">Email</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="auth-email"
            className="w-full bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-race-txt" />
        </div>
        <div>
          <label className="overline block mb-1">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="auth-password"
            className="w-full bg-race-bg border border-race-line focus:border-race-red outline-none px-3 py-2.5 rounded-sm text-race-txt" />
          {isLogin && !admin && (
            <div className="text-right mt-2">
              <Link to="/forgot-password" className="text-xs text-race-sub hover:text-race-red transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
          )}
        </div>
        {error && <p className="text-race-red text-sm" data-testid="auth-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="auth-submit"
          className="bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          {busy ? "…" : admin ? "ENTER ADMIN" : isLogin ? "ENTER" : "CREATE ACCOUNT"}
        </button>
      </form>
      {!admin && (
        <p className="text-center text-sm text-race-sub mt-4">
          {isLogin ? "New to Text Racing? " : "Already licensed? "}
          <Link to={(isLogin ? "/register" : "/login") + (next ? `?next=${encodeURIComponent(next)}` : "")} className="text-race-red hover:underline" data-testid="auth-switch">
            {isLogin ? "Create an account" : "Sign in"}
          </Link>
        </p>
      )}
    </div>
  );
}
